import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@safetyhub/ui';
import { memberDedupApi } from '../../../../lib/memberDedup';
import { MemberDedupModal } from './MemberDedupModal';

interface MemberDedupEntryProps {
  companyId: string;
  /** 병합이 멤버십을 닫으므로 처리 후 멤버 목록을 갱신한다 */
  onChanged?: () => void;
}

/**
 * "중복 인원 정리" 진입 버튼 (P5 대표 1차 판정).
 *
 * 노출 조건은 서버 판정에 위임한다 — 후보 목록 API가 클레임 조직 OWNER가 아니거나
 * 회사가 미클레임이면 403을 내므로, 조회 실패·후보 0건이면 조용히 렌더하지 않는다.
 * (isOwner 게이트는 멤버 관리 페이지 자체가 이미 건다 — useMembersState의 navigate('/'))
 */
export function MemberDedupEntry({ companyId, onChanged }: MemberDedupEntryProps) {
  const [candidateCount, setCandidateCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  /** 늦게 온 앞선 요청이 최신 결과를 덮지 않게 하는 순번 */
  const requestRef = useRef(0);

  const refreshCount = useCallback(async () => {
    const requestId = ++requestRef.current;
    try {
      const result = await memberDedupApi.listCandidates(companyId);
      if (requestRef.current === requestId) setCandidateCount(result.pairs.length);
    } catch {
      // 미클레임/권한 없음(403) 포함 — 진입점 자체를 숨긴다
      if (requestRef.current === requestId) setCandidateCount(0);
    }
  }, [companyId]);

  useEffect(() => {
    void refreshCount();
  }, [refreshCount]);

  if (candidateCount === 0 && !showModal) return null;

  return (
    <>
      <Button variant="secondary" onClick={() => setShowModal(true)}>
        중복 인원 정리 {candidateCount > 0 ? `(${candidateCount})` : ''}
      </Button>
      <MemberDedupModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          // 모달에서 병합/별개 처리한 만큼 남은 후보가 줄어든다 — 버튼의 건수(와 노출 여부)를
          // 다시 읽지 않으면 처리 전 숫자가 그대로 남는다.
          void refreshCount();
        }}
        companyId={companyId}
        onChanged={onChanged}
      />
    </>
  );
}
